import type { CSSObject, SerializedStyles } from '@emotion/react'
import classNames from 'classnames'
import React, { useMemo } from 'react'
import TextAreaAutosize, {
  TextareaAutosizeProps,
} from 'react-textarea-autosize'
import { useTheme } from '@opallabs/hooks'
import { WithTheme } from '../../../gem'
import { ErrorMessage } from '../../TextInput/errorMessage'
import * as styles from '../styles'

type AutoExpandTextAreaProps = Readonly<
  WithTheme & {
    counterStyleOverrides?: CSSObject
    error?: string
    errorStyleOverrides?: CSSObject
    /**
     * Controls how the character counter is displayed when
     * `maxLength` is set.
     *
     * Set to `true` to show it, `false` to hide, and 'onFocus'
     * to only show when focused. Default is `true`.
     */
    showCounter?: boolean | 'onFocus'
    styleOverrides?: CSSObject | SerializedStyles
    themeOverrides?: styles.AutoExpandTextAreaThemeOverrides
    /**
     * `truncate` requires a value for `maxRows` so that the
     * textarea will truncate the text when it reaches the
     * maximum number of rows.
     */
    truncate?: boolean
  }
>

type LibraryProps = TextareaAutosizeProps &
  (
    | React.RefAttributes<HTMLTextAreaElement>
    | React.MutableRefObject<HTMLTextAreaElement>
  )

export const AutoExpandTextArea = React.forwardRef<
  HTMLTextAreaElement,
  AutoExpandTextAreaProps & LibraryProps
>((props: AutoExpandTextAreaProps & LibraryProps, ref) => {
  const {
    counterStyleOverrides,
    error,
    errorStyleOverrides,
    showCounter = true,
    styleOverrides,
    theme: propTheme,
    themeOverrides,
    truncate = false,
    ...textAreaProps
  } = props
  const providedTheme = useTheme()
  const theme = propTheme ?? providedTheme

  const valueLength = useMemo(
    () =>
      textAreaProps.value ? textAreaProps.value.toLocaleString().length : 0,
    [textAreaProps.value]
  )

  const handleBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
    if (truncate && textAreaProps.maxRows) {
      event.currentTarget.scrollTop = 0
    }
    if (textAreaProps.onBlur) {
      textAreaProps.onBlur(event)
    }
  }

  return (
    <div
      className={classNames(
        styles.selectors.autoResizeContainer,
        'lapidary-expand-textarea-wrapper'
      )}
      style={{ display: 'flex', position: 'relative' }}
    >
      <TextAreaAutosize
        css={[
          styles.textArea({
            theme,
            themeOverrides,
            truncate: truncate,
            showCounterOnlyOnFocus: showCounter === 'onFocus',
            maxLength: textAreaProps.maxLength ? textAreaProps.maxLength : null,
            maxRows: textAreaProps.maxRows ? textAreaProps.maxRows : undefined,
          }),
          error ? styles.error(theme) : null,
          styleOverrides,
        ]}
        {...textAreaProps}
        // @hack override global styles
        className={classNames([
          'styled',
          styles.selectors.autoResizeInput,
          textAreaProps.className,
        ])}
        onBlur={handleBlur}
        onFocus={(e) => {
          // Sets cursor to end of the textarea
          setTimeout(() =>
            e.target.setSelectionRange(
              textAreaProps.value?.toString().length ?? null,
              textAreaProps.value?.toString().length ?? null
            )
          )
        }}
        ref={ref}
      />
      {valueLength !== null &&
        textAreaProps.maxLength &&
        showCounter !== false && (
          <span css={[styles.characterCounter(theme), counterStyleOverrides]}>
            {textAreaProps.maxLength -
              (textAreaProps.value?.toLocaleString().length
                ? textAreaProps.value?.toLocaleString().length
                : 0)}
          </span>
        )}
      <ErrorMessage
        message={error}
        styleOverrides={errorStyleOverrides}
        theme={theme}
      />
    </div>
  )
})
AutoExpandTextArea.displayName = 'Lapidary: AutoExpandTextArea'
